'use client';

import React, { ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Box,
  Tab,
  Tabs,
  TabScrollButton,
  TabScrollButtonProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { withErrorBoundary } from '@peerless/ui-shared';

export interface ProductTabsProps {
  /**
   * The tabs to render. The first tab is active by default.
   */
  tabs: ProductTab[];
}

export interface ProductTab {
  /**
   * The key used to identify the tab in the query string.
   */
  key: string;

  /**
   * The label of the tab. If not provided, the key is used as a translation key.
   */
  label?: ReactNode;

  /**
   * The content of the tab.
   */
  content: ReactNode;
}

export function ProductTabs({ tabs }: ProductTabsProps) {
  const t = useTranslations('Product');
  const id = useId();

  const [activeIndex, setActiveIndex] = useState(0);

  const onVisibilityChange = useCallback(
    (visible: string[]) => {
      if (visible.length > 0) {
        // find the first visible tab content
        const index = Math.min(...visible.map(id => tabs.findIndex(({ key }) => key === id)));

        if (index >= 0) {
          const tab = tabs[index];

          // set the active tab
          setActiveIndex(index);

          // set the url hash
          history.replaceState(null, '', `#${tab.key}`);
        }
      }
    },
    [tabs, setActiveIndex]
  );

  const { watch, scrollTo } = useVisibleElements(onVisibilityChange);

  const { onPrev, onNext } = useMemo(() => {
    function activate(index: number) {
      setActiveIndex(index);
      scrollTo(tabs[index].key);
    }

    return {
      onPrev() {
        activate(Math.max(0, activeIndex - 1));
      },
      onNext() {
        activate(Math.min(tabs.length - 1, activeIndex + 1));
      },
    };
  }, [tabs, activeIndex, setActiveIndex, scrollTo]);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ position: 'relative' }}>
      <Tabs
        variant={isSmallScreen ? 'scrollable' : 'fullWidth'}
        centered={!isSmallScreen}
        scrollButtons="auto"
        allowScrollButtonsMobile
        value={activeIndex}
        aria-label={t('tabs-label')}
        ScrollButtonComponent={TabScrollButtonWithNavigation}
        TabScrollButtonProps={{ onPrev, onNext } as TabScrollButtonWithNavigationProps}
        sx={{
          '.MuiTab-root': {
            width: { xs: '100%', md: 'auto' },
            maxWidth: { xs: '100%', md: 'auto' },
            minHeight: '4rem',
            borderBottom: '4px solid',
            borderColor: 'gray.light',
          },
          '.MuiTabs-indicator': {
            height: '4px',
          },
          position: 'sticky',
          top: { xs: '4rem', md: '6rem' },
          backgroundColor: 'common.white',
          zIndex: '2',
          minHeight: '4rem',
        }}
      >
        {tabs.map(({ key, label }) => (
          <Tab
            key={key}
            component={'a'}
            id={`product:tab${id}${key}`}
            label={label ?? t(key as never)}
            href={`#${key}`}
            aria-controls={key}
            sx={{ fontSize: '1.25rem', fontWeight: 600 }}
            onClick={e => {
              e.preventDefault();
              scrollTo(key);
            }}
          />
        ))}
      </Tabs>
      {tabs.map(({ key, content }) => (
        <TabContainer key={key} id={key} labelId={`product:tab${id}${key}`} watch={watch}>
          {content}
        </TabContainer>
      ))}
    </Box>
  );
}

type TabScrollButtonWithNavigationProps = TabScrollButtonProps & {
  onPrev(): void;
  onNext(): void;
};

function TabScrollButtonWithNavigation({
  onPrev,
  onNext,
  onClick,
  ...props
}: TabScrollButtonWithNavigationProps) {
  const isNext = !!props.slotProps?.endScrollButtonIcon;

  const handleClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    e => {
      // pass along the event to trigger the tabs container scroll
      onClick?.(e);

      // trigger the tab navigation
      if (isNext) {
        onNext();
      } else {
        onPrev();
      }
    },
    [isNext, onClick, onPrev, onNext]
  );

  return <TabScrollButton onClick={handleClick} {...props} />;
}

type TabContainerProps = React.PropsWithChildren<{
  id: string;
  labelId: string;
  watch(target: Element): () => void;
}>;

const TabContainer = withErrorBoundary(function TabContainer({
  id,
  labelId,
  watch,
  children,
}: TabContainerProps) {
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const current = container.current;
    if (current) {
      return watch(current);
    }
  }, [id, watch]);

  return (
    <Box
      ref={container}
      id={id}
      role="tabpanel"
      aria-labelledby={labelId}
      sx={{
        scrollMarginTop: { xs: '8rem', md: '10rem' },
        py: 4,
        position: 'relative',
        zIndex: '1',
      }}
    >
      {children}
    </Box>
  );
});

/**
 * Watches the visibility of elements as they are scrolled into view or resized and calls the provided callback.
 */
function useVisibleElements(onVisibilityChange: (visible: string[]) => void) {
  const idset = useRef(new Set<string>());

  const onIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          // If the element is intersecting, add it to the set of visible elements.
          idset.current.add(entry.target.id);
        } else {
          // Otherwise, remove it from the set of visible elements.
          idset.current.delete(entry.target.id);
        }
      }

      onVisibilityChange(Array.from(idset.current));
    },
    [onVisibilityChange]
  );

  // we need to watch resizes as well, because the spex widget content will resize itself as it loads
  const onResize = useCallback(
    (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        if (isElementInViewport(entry.target)) {
          // If the element is in the viewport, add it to the set of visible elements.
          idset.current.add(entry.target.id);
        } else {
          // Otherwise, remove it from the set of visible elements.
          idset.current.delete(entry.target.id);
        }
      }

      onVisibilityChange(Array.from(idset.current));
    },
    [onVisibilityChange]
  );

  /** Intersection Observer */
  const io = useMemo(
    () =>
      typeof IntersectionObserver === 'undefined' // does not exist for SSR
        ? null
        : new IntersectionObserver(onIntersection, {
            // check that the element is scrolled into view by 200px
            rootMargin: `-200px 0px -200px`,
          }),
    [onIntersection]
  );

  /** Resize Observer */
  const ro = useMemo(
    () =>
      typeof ResizeObserver === 'undefined' // does not exist for SSR
        ? null
        : new ResizeObserver(onResize),
    [onResize]
  );

  /** Watch Function - Used to register an Element for visibility checks. */
  const watch = useCallback(
    (target: Element) => {
      io?.observe(target);
      ro?.observe(target);
      return () => {
        io?.unobserve(target);
        ro?.unobserve(target);
        // make sure the element is removed from the set of visible elements
        idset.current.delete(target.id);
      };
    },
    [io, ro]
  );

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'instant' });
  }, []);

  useEffect(() => {
    return () => {
      io?.disconnect();
      ro?.disconnect();
    };
  }, [io, ro]);

  return { watch, scrollTo };

  function isElementInViewport(el: Element) {
    const rect = el.getBoundingClientRect();

    return (
      rect.bottom >= 0 &&
      rect.right >= 0 &&
      rect.top <= (window.innerHeight || document.documentElement.clientHeight)
    );
  }
}
